import {
  formatTimezoneLocalDateTime,
  TIMEZONE_OPTIONS,
  type AllowedTimezone,
  type AppTimezonePreference,
} from '@sparkai/utils'

import { DatePickerProps } from '../../Form/DatePickers'

export type DateRangeTuple = [Date | null, Date | null]
export type DateFilterValue = [string | null, string | null, string?, string?]
export type DateRangeSelectOption = {
  value: string
  requestValue?: string
  label: string
  displayLabel?: string
}
export type ActiveDateBoundary = 'start' | 'end'
export type DatePickerRangeProps = Omit<DatePickerProps, 'value' | 'onChange'> & {
  value?: Date | null
  onChange: (value: DateRangeTuple | Date | null) => void
  selectsRange: boolean
  startDate?: Date | null
  endDate?: Date | null
}

export function isSameDay(first: Date | null, second: Date | null): boolean {
  return Boolean(
    first &&
    first.getFullYear() === second?.getFullYear() &&
    first.getMonth() === second?.getMonth() &&
    first.getDate() === second?.getDate()
  )
}

const DATE_TIME_WITH_ZONE_PATTERN = /^(.+?)(?:z|[+-]\d{2}:?\d{2})$/i

function getAllowedTimezone(timezone: string): AllowedTimezone {
  return TIMEZONE_OPTIONS.some((option) => option.value === timezone) ? (timezone as AllowedTimezone) : 'UTC'
}

function getTimezonePreference(timezone: string): AppTimezonePreference {
  const allowedTimezone = getAllowedTimezone(timezone)

  return {
    timezoneMode: allowedTimezone === 'UTC' ? 'UTC' : 'IANA',
    timezone: allowedTimezone,
    timeFormat: '24h',
  }
}

function toDisplayDateValue(value: string, timezone?: string): Date {
  if (!timezone || !DATE_TIME_WITH_ZONE_PATTERN.test(value)) return new Date(value)

  return new Date(formatTimezoneLocalDateTime(value, getTimezonePreference(timezone)))
}

export function toDateRangeTuple(value: DateFilterValue, timezone?: string): DateRangeTuple {
  return [
    value[0] ? toDisplayDateValue(value[0], timezone) : null,
    value[1] ? toDisplayDateValue(value[1], timezone) : null,
  ]
}

function toLocalDateTimeValue(date: Date, isEndBoundary?: boolean): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = isEndBoundary ? '59' : '00'
  const milliseconds = isEndBoundary ? '.999' : '.000'

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${milliseconds}`
}

export function formatDateValue(
  date: Date,
  useLocalDateTimeValue?: boolean,
  isEndBoundary?: boolean
): string {
  return useLocalDateTimeValue ? toLocalDateTimeValue(date, isEndBoundary) : date.toISOString()
}

export function formatDateRangeValue(
  value: DateRangeTuple,
  useLocalDateTimeValue?: boolean,
  timezoneRequestValue?: string
): DateFilterValue {
  const dateValues: DateFilterValue = [
    value[0] ? formatDateValue(value[0], useLocalDateTimeValue) : null,
    value[1] ? formatDateValue(value[1], useLocalDateTimeValue, true) : null,
  ]

  if (timezoneRequestValue) dateValues[2] = timezoneRequestValue

  return dateValues
}

export function getTimezoneSelectValue(
  options: DateRangeSelectOption[],
  value?: string,
  defaultValue?: string
): string {
  const currentValue = value ?? defaultValue
  if (!currentValue) return ''

  return (
    options.find((option) => option.value === currentValue || option.requestValue === currentValue)?.value ??
    ''
  )
}

export function getTimezoneRequestValue(options: DateRangeSelectOption[], value?: string): string {
  const selectedOption = options.find((option) => option.value === value)
  return selectedOption?.requestValue ?? value ?? ''
}

export function getDatePickerTimeFormat(timeFormat: '12h' | '24h') {
  return timeFormat === '12h' ? 'h:mm aa' : 'HH:mm'
}

export function getDateFilterValue(value: unknown): DateFilterValue {
  if (!Array.isArray(value)) return [null, null]

  const [start, end, timezone] = value
  return [
    typeof start === 'string' ? start : null,
    typeof end === 'string' ? end : null,
    typeof timezone === 'string' ? timezone : undefined,
  ]
}

export function getSelectedStringValues(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const stringValues = Array.isArray(value[0]) ? value[0] : value
  return stringValues.filter((item): item is string => typeof item === 'string')
}
